import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import { Layout } from '../components/Layout/Layout';
import { Button } from '../components/common/Button';
import { useNavigation } from '../hooks/useNavigation';
import { setSelectedFrameIndex } from '../store/photoSlice';
import { RootState } from '../store/rootReducer';
import { Frame } from '../types/api.v2.types';
import { useAiImageAnalytics } from '../hooks/useAiImageAnalytics';
import { usePagination } from '../hooks/usePagination';
import { ArrowLeftIcon, ArrowRightIcon } from '../components/icons/Icons';
import {
  FrameGridContainer,
  ChooseFrameBox,
  DashedBox,
  Container,
  FrameItem,
  FramePreviewBox,
  FrameSelectGuideText,
} from './ChooseFramePage.styled.ts';
import { PaginationButton, PageStatus } from './ThemePage.styled.ts';

const emptyFrame: Frame = {
  frameId: 0,
  sample: '',
  real: '',
  displayPhotoPositions: [],
  displayQrCodePosition: { left: 0, top: 0 },
  printPhotoPositions: [],
  printQrCodePosition: { left: 0, top: 0 },
};

const FRAMES_PER_PAGE = 12;

export function ChooseFramePage() {
  const { t } = useTranslation();
  const emotionTheme = useTheme();
  const block = useSelector((state: RootState) => state.block.block);
  const selectedThemeId = useSelector((state: RootState) => state.theme.selectedThemeId);
  const selectedFrameIndex = useSelector((state: RootState) => state.photo.selectedFrameIndex);
  const dispatch = useDispatch();
  const { navigateToChoosePhoto } = useNavigation();
  const { recordEntryData } = useAiImageAnalytics();

  const theme =
    selectedThemeId && block?.themes
      ? block.themes.find((t) => t.id === selectedThemeId) || block.themes[0]
      : block?.themes?.[0];

  const frames = theme?.frames || [];

  const { currentPage, totalPages, goToNextPage, goToPrevPage } = usePagination({
    totalItems: frames.length,
    itemsPerPage: FRAMES_PER_PAGE,
  });

  const currentPageFrames = frames.slice(
    (currentPage - 1) * FRAMES_PER_PAGE,
    currentPage * FRAMES_PER_PAGE
  );

  const currentFrames = [
    ...currentPageFrames,
    ...Array(Math.max(0, FRAMES_PER_PAGE - currentPageFrames.length)).fill({ ...emptyFrame }),
  ];

  const selectedFrame = frames[selectedFrameIndex] || frames[0];

  useEffect(() => {
    recordEntryData();
  }, [recordEntryData]);

  const handleFrameSelect = (index: number) => {
    const actualIndex = (currentPage - 1) * FRAMES_PER_PAGE + index;
    if (actualIndex < frames.length) {
      dispatch(setSelectedFrameIndex(actualIndex));
    }
  };

  return (
    <Layout>
      <Container>
        <div>
          <FrameSelectGuideText>{t('chooseFrame.selectFrame')}</FrameSelectGuideText>
          <DashedBox>
            <PaginationButton onClick={goToPrevPage} disabled={currentPage === 1}>
              <ArrowLeftIcon disabled={currentPage === 1} theme={emotionTheme} size={40} />
            </PaginationButton>
            <ChooseFrameBox>
              <FrameGridContainer>
                {currentFrames.map((frame, index) => (
                  <FrameItem
                    key={index}
                    selected={(currentPage - 1) * FRAMES_PER_PAGE + index === selectedFrameIndex}
                    onClick={() => handleFrameSelect(index)}
                    isEmpty={frame.sample === ''}
                  >
                    {frame.sample !== '' ? (
                      <img src={frame.sample} alt={`Frame ${frame.frameId}`} />
                    ) : (
                      <div className="empty-frame" />
                    )}
                  </FrameItem>
                ))}
              </FrameGridContainer>
            </ChooseFrameBox>
            <PaginationButton onClick={goToNextPage} disabled={currentPage === totalPages}>
              <ArrowRightIcon
                disabled={currentPage === totalPages}
                theme={emotionTheme}
                size={40}
              />
            </PaginationButton>
            <PageStatus>{`${currentPage} / ${totalPages}`}</PageStatus>
          </DashedBox>
        </div>

        <FramePreviewBox>
          <img src={selectedFrame.sample} alt={`Frame ${selectedFrame.frameId}`} />
          <Button onClick={navigateToChoosePhoto}>{t('chooseFrame.complete')}</Button>
          <p>{t('chooseFrame.reselect')}</p>
        </FramePreviewBox>
      </Container>
    </Layout>
  );
}
